<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"></div>

    <!-- Modal Container - Responsive -->
    <div class="flex min-h-full items-center justify-center md:p-4">
      <div class="relative bg-white dark:bg-slate-900 shadow-2xl w-full border-slate-200 dark:border-slate-800
                  md:rounded-2xl md:max-w-2xl md:border
                  h-full md:h-auto overflow-y-auto">

        <!-- Header - Mobile optimized -->
        <div class="px-4 md:px-6 py-4 md:py-5 border-b border-slate-200 dark:border-slate-800
                    bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30
                    md:rounded-t-2xl sticky top-0 z-10">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-indigo-500 flex items-center justify-center shrink-0 shadow-lg">
              <svg class="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h2 class="text-base md:text-xl font-bold text-slate-900 dark:text-white leading-tight">Welcome to Master Ledger!</h2>
              <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400 mt-0.5">Choose how you want to use this application</p>
            </div>
          </div>
        </div>

        <!-- Content - Mobile optimized spacing -->
        <div class="px-4 md:px-6 pt-4 md:pt-6 pb-4 md:pb-6 space-y-3 md:space-y-4">

          <!-- Business Mode Option - Mobile optimized -->
          <button @click="selectMode(true)" type="button"
            class="w-full flex items-start gap-3 md:gap-4 p-4 md:p-5 border-2 rounded-xl md:rounded-2xl transition-all text-left active:scale-[0.98] md:hover:shadow-lg"
            :class="selectedMode === true ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-md' : 'border-slate-300 dark:border-slate-700 active:border-blue-300 dark:active:border-blue-700'">
            <div class="flex-shrink-0 mt-0.5 md:mt-1">
              <div class="w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-all"
                :class="selectedMode === true ? 'border-blue-500 bg-blue-500' : 'border-slate-300 dark:border-slate-600'">
                <div v-if="selectedMode === true" class="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-white"></div>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <svg class="w-5 h-5 md:w-6 md:h-6 text-blue-600 dark:text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/>
                </svg>
                <h3 class="text-base md:text-lg font-bold text-slate-900 dark:text-white">Business Mode</h3>
              </div>
              <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400 mb-2 md:mb-3">
                Perfect for businesses, shops, and commercial operations
              </p>
              <ul class="space-y-1 md:space-y-1.5 text-xs md:text-sm text-slate-600 dark:text-slate-400">
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Create and manage invoices
                </li>
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Track payments and receivables
                </li>
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Manage customers and products
                </li>
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Access business reports and analytics
                </li>
              </ul>
            </div>
          </button>

          <!-- Personal Mode Option - Mobile optimized -->
          <button @click="selectMode(false)" type="button"
            class="w-full flex items-start gap-3 md:gap-4 p-4 md:p-5 border-2 rounded-xl md:rounded-2xl transition-all text-left active:scale-[0.98] md:hover:shadow-lg"
            :class="selectedMode === false ? 'border-green-500 bg-green-50 dark:bg-green-950/30 shadow-md' : 'border-slate-300 dark:border-slate-700 active:border-green-300 dark:active:border-green-700'">
            <div class="flex-shrink-0 mt-0.5 md:mt-1">
              <div class="w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-all"
                :class="selectedMode === false ? 'border-green-500 bg-green-500' : 'border-slate-300 dark:border-slate-600'">
                <div v-if="selectedMode === false" class="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-white"></div>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <svg class="w-5 h-5 md:w-6 md:h-6 text-green-600 dark:text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
                </svg>
                <h3 class="text-base md:text-lg font-bold text-slate-900 dark:text-white">Personal Mode</h3>
              </div>
              <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400 mb-2 md:mb-3">
                Ideal for personal finance tracking and wealth management
              </p>
              <ul class="space-y-1 md:space-y-1.5 text-xs md:text-sm text-slate-600 dark:text-slate-400">
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Track personal income and expenses
                </li>
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Manage bank accounts
                </li>
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Monitor your wealth and assets
                </li>
                <li class="flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 md:w-4 md:h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                  Simple, focused interface
                </li>
              </ul>
            </div>
          </button>

        </div>

        <!-- Footer - Mobile optimized with fixed bottom -->
        <div class="fixed md:relative bottom-0 left-0 right-0 md:bottom-auto md:left-auto md:right-auto
                    px-4 md:px-6 pt-4 pb-4 md:py-4
                    bg-white dark:bg-slate-900 md:bg-slate-50 md:dark:bg-slate-800/50
                    border-t border-slate-200 dark:border-slate-800 md:border-t
                    md:rounded-b-2xl
                    flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-0 md:justify-between
                    shadow-[0_-4px_12px_rgba(0,0,0,0.1)] md:shadow-none
                    z-20">
          <p class="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 text-center md:text-left">
            You can change this anytime in Settings → Account Mode
          </p>
          <button @click="confirmSelection" :disabled="selectedMode === null"
            class="bg-indigo-600 active:bg-indigo-700 md:hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                   text-white font-semibold rounded-lg md:rounded-lg px-6 py-3 md:py-2.5 text-sm transition
                   shadow-lg shadow-indigo-200 dark:shadow-indigo-900/50
                   active:scale-[0.98] md:active:scale-100">
            Continue
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const selectedMode = ref(null)

function selectMode(mode) {
  selectedMode.value = mode
}

function confirmSelection() {
  if (selectedMode.value === null) return

  useForm({ user: { business_mode: selectedMode.value } }).patch('/settings/business_mode', {
    preserveScroll: true,
    onSuccess: () => {
      // Inertia will handle the redirect from the server
      // The page will reload with the new business_mode setting
    }
  })
}
</script>
